import { Grid, Stack, Paper, IconButton, Tooltip, Skeleton } from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseIcon from '@mui/icons-material/Close';
import React, { useState, useCallback, useEffect, useRef } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer  } from 'recharts';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import RouteIcon from '@mui/icons-material/Route';
import { useSelector, useDispatch } from 'react-redux';
import { Box } from '@mui/system';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography
} from '@mui/material';
import DownloadIcon from '../../../assets/img/DownloadIcon'
import DatePicker from 'react-date-picker';
import moment from 'moment';
import {
  ArrowBackIosOutlined, ArrowForwardIosOutlined, CheckCircleOutline as CheckCircleOutlineIcon,
  ErrorOutline as ErrorOutlineIcon
} from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import PsychologyIcon from '@mui/icons-material/Psychology';
import AIAnalysisModal from '../../Modal/AIAnalysisModal';
import { getParameterAIAnalysis } from '../../../services/aiAnalysisService';

import { useHealthParamsCache } from '../../../hooks/useCache';
import { useRingSSE } from '../../../hooks/useRingSSE';
import {
  bloodSugar,
  bloodPressure,
  bloodOxygen,
  bodyTemp,
  heartRate,
  respiratoryRate,
  healthParams,
  deleteHealthParams,
  getStepsData,
  getSleepData,
} from '../../../redux/user/userActions';

const DateRangeGrid = styled(Grid)(({ }) => ({
  height: '30px',
  '& .react-date-picker__calendar': {
    width: '260px',
    minHeight: '256px'
  },
  '& .react-date-picker__clear-button, .react-date-picker__calendar-button, .react-date-picker__inputGroup__input, .react-date-picker__inputGroup__divider, .react-date-picker__inputGroup__leadingZero': {
    display: 'none'
  },
  '& .react-date-picker__wrapper': {
    minHeight: 20,
    maxHeight: 30,
    border: 'none !important',
    justifyContent: 'center',
    alignContent: 'center',
    margin: 'auto',
    '& .react-date-picker__inputGroup': {
      height: '20px',
      cursor: 'pointer',
      // background: 'brown',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      '& input[name="date"]': {
        height: '20px',
      }
    }
  },
  '& .react-calendar__month-view__weekdays': {
    display: 'none !important'
  },
  '& .react-calendar__tile--now': {
    background: '#FFF !important',
    color: '#000 !important'
  },
  '& .react-calendar__tile--active': {
    background: 'linear-gradient(90deg, #097EB7 0%, #2AAB6C 100%) !important',
    borderRadius: '50%',
    color: "#FFF !important"
  },
  '& .react-calendar': {
    width: '260px',
  },
  '& .react-calendar__navigation': {
    marginBottom: '10px'
  },
  '& .react-calendar__year-view__months': {
    '& .react-calendar__year-view__months__month': {
      height: '50px',
      padding: '0px !important'
    }
  },
  '& .react-calendar__navigation__next2-button, .react-calendar__navigation__prev2-button': {
    display: 'none'
  },
  '& .react-calendar__month-view__days__day--weekend': {
    color: '#000'
  },
  '& .react-calendar__month-view__days__day--neighboringMonth': {
    opacity: 0,
    pointerEvents: 'none'
  },
  '& .react-calendar__decade-view__years__year, .react-calendar__century-view__decades__decade': {
    height: '46px !important',
    padding: '1.5em 0.5em !important'
  },
  '& .react-calendar__century-view__decades__decade': {
    height: '58px !important',
  },
  '& .react-date-picker__calendar--open': {
    position: 'absolute',
    top: '0px !important',
    '& .react-calendar': {
      minHeight: '250px',
      border: 'none'
    }
  },
}))

const diaColors = {
  "1": "#2AAB6C",
  "2": "#EAAD11",
  "3": "#F36363",
}
const DiabetesRange = styled('span', { shouldForwardProp: (prop) => prop !== 'type' })(({ type }) => ({
  height: '16px',
  fontFamily: 'Poppins',
  fontStyle: 'normal',
  fontWeight: '500',
  fontSize: '12px',
  lineHeight: '16px',
  '-webkit-text-fill-color': 'initial',
  color: diaColors[type],
}))
const customShape = (key, props) => {
  const { x, y, width, height, graphvalue } = props;
  const borderRadius = 5;
  let fillColor = "url(#colorUv)"
  if (key === 'bloodPressureData') {
    fillColor = "url(#colorUvBloodPressure)"
  }
  if (graphvalue === 0) {
    return <path />
  }
  return (
    <path d={`M${x},${y + borderRadius}a${borderRadius},${borderRadius} 0 0 1 ${borderRadius},${-borderRadius}h${width - borderRadius * 2}a${borderRadius},${borderRadius} 0 0 1 ${borderRadius},${borderRadius}v${height - borderRadius * 2}a${borderRadius},${borderRadius} 0 0 1 ${-borderRadius},${borderRadius}h${-width + borderRadius * 2}a${borderRadius},${borderRadius} 0 0 1 ${-borderRadius},${-borderRadius}z`} fill={fillColor} />
  );
};

const names = {
  T: ['3', '6', '9', '12', '15', '18', '21', '24'],
  W: ['M', 'T', 'W', 'T', 'F', 'S', 'S'],
  M: ['5', '10', '15', '20', '25', '30'],
  Y: ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'],
}

const findUpperAndLowerRange = (num, maxVal, isMin) => {
  let min = num - (maxVal > 500 ? 3.5 : 3.1) * (maxVal / 100);
  let max = num + (maxVal > 500 ? 3.5 : 3.1) * (maxVal / 100)
  if (min < 0) {
    max += min
    min = 0
  }
  return isMin ? min : max
}

function formatGraphData(key, duration = 'W', minMaxDate, noAvg) {
  if (duration) duration = duration.toUpperCase()
  let data = JSON.parse(JSON.stringify(this))

  // Ensure data is an array
  if (!Array.isArray(data)) {
    return []
  }

  const allKeys = key.split('&')
  if (duration === 'W' && minMaxDate[0] && minMaxDate[1]) {
    const startDate = new Date(minMaxDate[0])
    startDate.setHours(0, 0, 0, 0)
    const endDate = new Date(minMaxDate[1])
    endDate.setHours(23, 59, 59, 999)
    data = data.filter(x => new Date(x.createdAt).getTime() >= startDate.getTime() && new Date(x.createdAt).getTime() <= endDate.getTime())
  }
  data.forEach(x => {
    if (!x[allKeys[0]] && !noAvg) return
    x.graphvalue = []
    if (noAvg) {
      x.graphvalue = [x[key], x[key]]
    } else if (allKeys.length > 1) {
      allKeys.forEach(element => {
        x.graphvalue.push(x[element])
      });
    } else {
      x.graphvalue = x[key]
    }
  })
  let result = []
  names[duration].forEach((x, i) => {
    result[i] = { name: x, graphvalue: 0, uniqueIdentifies: i, count: 0 }
  })

  const recordCount = {}
  data.forEach((cur, ind) => {
    let selector = new Date(cur.createdAt).getDay()
    if (duration === 'Y') {
      selector = new Date(cur.createdAt).getMonth()
    } else if (duration === 'M' || duration === 'T') {
      let selected = duration === 'T' ? new Date(cur.createdAt).getHours() : new Date(cur.createdAt).getDate()
      const upper = +names[duration][names[duration].length - 1]
      const lower = +names[duration][0]
      if (selected && duration === 'M' && selected > 30) selected = 30

      const { [duration]: _name } = names
      let i = 0
      while (i < _name.length) {
        const selection = +_name[i]
        const preSelection = i === 0 ? 0 : +_name[i - 1]
        if (selected > preSelection && selected <= selection) {
          selector = i
          break;
        }
        i++
      }
    } else if (duration === 'W') {
      if (selector === 0) selector = 6
      else selector -= 1
    }
    cur.name = names[duration][selector]
    if (!result[selector]?.graphvalue) {
      result[selector] = cur
      if (!result[selector].count) result[selector].count = 1
    }
    if (Object.prototype.toString.call(cur.graphvalue) === '[object Array]') {
      if (result[selector]?.graphvalue[0] > cur.graphvalue[0]) {
        result[selector].graphvalue[0] = cur.graphvalue[0]
      } else if (result[selector]?.graphvalue[1] < cur.graphvalue[1]) {
        result[selector].graphvalue[1] = cur.graphvalue[1]
      }
    } else {
      if (result[selector]._id !== cur._id) {
        result[selector].graphvalue += cur.graphvalue
        result[selector].count += 1
      }
      recordCount[selector] = recordCount[selector] ? recordCount[selector] + 1 : 1
    }
  })
  let max = 0;
  result.forEach(x => {
    const value = Object.prototype.toString.call(x.graphvalue) === '[object Number]' ? x.graphvalue : Object.prototype.toString.call(x.graphvalue) === '[object Array]' ? (x.graphvalue[0] + x.graphvalue[1]) / 2 : 0
    if ((value / x.count) > max) {
      max = (value / x.count)
    }
  })
  const finalResult = result.map(({ graphvalue, ...rest }) => {
    let currentValue = []
    if (Object.prototype.toString.call(graphvalue) === '[object Number]' && graphvalue !== 0) {
      const value = graphvalue / (rest.count || 1)
      currentValue[0] = findUpperAndLowerRange(value, max, true)
      currentValue[1] = findUpperAndLowerRange(value, max, false)
    } else if (Object.prototype.toString.call(graphvalue) === '[object Array]' && graphvalue[0] === graphvalue[1]) {
      currentValue[0] = findUpperAndLowerRange(graphvalue[0], max, true)
      currentValue[1] = findUpperAndLowerRange(graphvalue[1], max, false)
    } else {
      currentValue = graphvalue
    }
    return { ...rest, graphvalue: currentValue }
  })
  return finalResult.filter((x) => x)
}

function getAvg(key, isTemp = false, duration = 'W', minMaxDate = [], isRange, formatGraphDataValues, isRangeAvg) {
  let data = JSON.parse(JSON.stringify(this))

  // Ensure data is an array
  if (!Array.isArray(data)) {
    return '-'
  }

  if (duration.toUpperCase() === 'W' && minMaxDate[0] && minMaxDate[1]) {
    data = data.filter(x => new Date(x.createdAt).getTime() >= new Date(minMaxDate[0]).getTime() && new Date(x.createdAt).getTime() <= new Date(minMaxDate[1]).getTime())
  }
  if (isTemp) {
    data.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    const scale = +data?.[0]?.temperature_unit === 3 ? 'F' : 'C'
    const value = data?.[0]?.['temperature']
    if (!(value && +value)) return '-'
    if (key === 'fahrenheit') {
      return scale === 'F' ? value : ((value * 9 / 5) + 32).toFixed(2)
    }
    return scale === 'F' ? ((value - 32) * 5 / 9).toFixed(2) : value
  } else if (isRange || isRangeAvg) {
    let min
    let max
    let count = 0
    if (key === 'sys_blood_pressure') {
      const systolicKey = 'sys_blood_pressure';
      const diastolicKey = 'dia_blood_pressure';
      if (Array.isArray(data) && data.length > 0) {
        min = +((data.reduce((prev, curr) => prev + curr[systolicKey], 0) / data.length).toFixed(2)) || '-';
        max = +((data.reduce((prev, curr) => prev + curr[diastolicKey], 0) / data.length).toFixed(2)) || '-';
      } else {
        min = '-';
        max = '-';
      }
    }
    else {
      formatGraphDataValues.forEach((x) => {
        if (x.graphvalue && x.graphvalue[0]) {
          let changes = false
          if (isRangeAvg) {
            if (x.graphvalue[0] && x.graphvalue[1]) {
              min = (min || 0) + x.graphvalue[0]
              max = (max || 0) + x.graphvalue[1]
              count += 1
            }
          } else {
            if (!min) min = x.graphvalue[0]
            if (!max) max = x.graphvalue[1]
            if (min > x.graphvalue[0]) {
              min = x.graphvalue[0]
            }
            if (max < x.graphvalue[1]) {
              max = x.graphvalue[1]
            }
          }
        }
      })
    }
    if (count > 0) {
      return min && max ? `${Math.round(min / count)}-${Math.round(max / count)}` : '-'
    }
    return min && max ? `${Math.round(min)}-${Math.round(max)}` : '-'
  }

  // Final array validation before reduce
  if (Array.isArray(data) && data.length > 0) {
    return +((data.reduce((prev, curr) => prev + curr[key], 0) / data.length).toFixed(2)) || '-'
  }
  return '-'
}

const headingText = {
  'w': 'Weekly',
  'm': 'Monthly',
  'y': 'Yearly',
  't': 'Today',
  'd': 'Daily'
}

const getBottomValueForPendulam = (value) => {
  if (value <= 30) {
    return 4
  } else if (value <= 38) {
    const oneDegreeInPx = 56 / 8
    return (oneDegreeInPx * (value - 29.5))
  } else if (value <= 39) {
    const oneDegreeInPx = 146 - 73
    return 60 + (oneDegreeInPx * ((value - 38) || .01))
  } else if (value <= 41) {
    const oneDegreeInPx = 175 - 146
    return 146 + (oneDegreeInPx * ((value - 39) || .01))
  }
  return 204
}

const ExportModal = ({ open, onClose, title, message, type }) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="sm"
    fullWidth
  >
    <DialogTitle sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      color: type === 'success' ? '#2AAB6C' : '#FF5252'
    }}>
      {type === 'success' ? (
        <CheckCircleOutlineIcon />
      ) : (
        <ErrorOutlineIcon />
      )}
      {title}
    </DialogTitle>
    <DialogContent>
      <Typography>
        {message}
      </Typography>
    </DialogContent>
    <DialogActions>
      <Button
        onClick={onClose}
        variant="contained"
        sx={{
          backgroundColor: type === 'success' ? '#2AAB6C' : '#FF5252',
          '&:hover': {
            backgroundColor: type === 'success' ? '#238B5B' : '#E04A4A'
          }
        }}
      >
        OK
      </Button>
    </DialogActions>
  </Dialog>
);

export const RenderList = React.memo(({ item, className, duration, setDuration, handleClick, date, setDate, isHideModalPop, patientID }) => {

  const userType = localStorage.getItem("userType");
  const [isOpen, setIsOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [lastAIAnalysisKey, setLastAIAnalysisKey] = useState(null)
  const [aiModalOpen, setAiModalOpen] = useState(false)
  const [aiData, setAiData] = useState(null)
  const healthParam = useSelector((state) => state?.user?.healthParam?.[0] || {});
  const dispatch = useDispatch()
  const { clearCurrentUser } = useHealthParamsCache()
  const { userData } = useSelector((state) => ({ userData: state.auth.userData }))
  const aiAnalysisState = useSelector((state) => state?.user?.aiAnalysis || {});
  const [exportSuccessModalOpen, setExportSuccessModalOpen] = useState(false);
  const [exportErrorModalOpen, setExportErrorModalOpen] = useState(false);
  const [exportMessage, setExportMessage] = useState('');
  const [tooltip, setTooltip] = React.useState(null);
const [sleepExpandOpen, setSleepExpandOpen] = useState(false);

  const [isCardLoading, setIsCardLoading] = useState(false);
  const cardLoadingMounted = useRef(false);
  const currentDuration = duration[item.primaryKey];
  const currentDate = date[item.primaryKey];
  const currentHealthParam = healthParam[item.primaryKey];

  useEffect(() => {
    if (!cardLoadingMounted.current) {
      cardLoadingMounted.current = true;
      return;
    }
    setIsCardLoading(true);
  }, [currentDuration, currentDate]);

  useEffect(() => {
    setIsCardLoading(false);
  }, [currentHealthParam]);



  const SLEEP_COLORS = {
    241: '#26C6DA',
    242: '#4A90D9',
    243: '#1A3A6B',
    244: '#FF6B6B',
  };

  const SLEEP_LABELS = {
    241: 'REM',
    242: 'Light',
    243: 'Deep',
    244: 'Awake',
  };

  const SleepChart = ({ data, date: selectedDate, duration, isExpanded = false }) => {

    if (!data || !Array.isArray(data) || data.length === 0) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 220 }}>
          <Typography variant="caption" color="textSecondary">No sleep data</Typography>
        </Box>
      );
    }

    const currentDur = (duration || 'd').toLowerCase();
    const targetMoment = moment(selectedDate);

    // A night belongs to the day it ENDED (backend `sleepDay`); fall back to
    // startTime only if the field is missing (older/uncached responses).
    const getRecordDate = (record) => record.sleepDay ? moment(record.sleepDay) : moment(record.startTime * 1000);

    // Attribute the WHOLE night to its wake-up day using the backend's
    // pre-aggregated stageTotals — never split a single night across two days.
    const getSleepSecsByType = (record, dateStr) => {
      const recDay = record.sleepDay || moment(record.startTime * 1000).format('YYYY-MM-DD');
      if (recDay !== dateStr) return { deepSec: 0, remSec: 0, lightSec: 0 };
      if (record.stageTotals) {
        return {
          deepSec: record.stageTotals.deepSec || 0,
          remSec: record.stageTotals.remSec || 0,
          lightSec: record.stageTotals.lightSec || 0,
        };
      }
      const segs = (record.sleepData || []).filter(seg => seg.sleepType !== 244);
      return {
        deepSec: segs.filter(s => s.sleepType === 243).reduce((sum, s) => sum + s.sleepLen, 0),
        remSec: segs.filter(s => s.sleepType === 241).reduce((sum, s) => sum + s.sleepLen, 0),
        lightSec: segs.filter(s => s.sleepType === 242).reduce((sum, s) => sum + s.sleepLen, 0),
      };
    };
    if (isExpanded && currentDur === 'd') {
      const selectedDateStr = targetMoment.format('YYYY-MM-DD');

      // Step 1: collect ALL sessions for this wake-up day (main night + any naps)
      const dayRecords = data.filter(r =>
        getRecordDate(r).format('YYYY-MM-DD') === selectedDateStr
      );

      if (dayRecords.length === 0) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, bgcolor: '#0D1B2A', borderRadius: '12px' }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>No sleep data for this day</Typography>
          </Box>
        );
      }
      const allSegments = [];
      dayRecords.forEach(record => {
        (record.sleepData || []).forEach(seg => {
          const rawStart = seg.sleepStartTime;
          const startMs = rawStart > 1e12 ? rawStart : rawStart * 1000;

          allSegments.push({
            type: seg.sleepType,
            start: startMs,
            end: startMs + seg.sleepLen * 1000,
            len: seg.sleepLen,
          });
        });
      });
      allSegments.sort((a, b) => a.start - b.start);

      if (allSegments.length === 0) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, bgcolor: '#0D1B2A', borderRadius: '12px' }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>No segments found</Typography>
          </Box>
        );
      }

      const LANES = {
        244: { label: 'Awake', color: '#E05F5F', idx: 0 },
        241: { label: 'REM', color: '#26C6DA', idx: 1 },
        242: { label: 'Light', color: '#4A90D9', idx: 2 },
        243: { label: 'Deep', color: '#1A3A6B', idx: 3 },
      };
      const LANE_ORDER = [244, 241, 242, 243];

      const SVG_W = 1400;
      const LABEL_W = 58;
      const RIGHT_PAD = 16;
      const CHART_W = SVG_W - LABEL_W - RIGHT_PAD;
      const LANE_H = 60;
      const TOP_PAD = 12;
      const BOTTOM_PAD = 52;
      const CHART_H = 4 * LANE_H;
      const SVG_H = TOP_PAD + CHART_H + BOTTOM_PAD;

      const dayStartMs = moment(selectedDateStr).startOf('day').valueOf();
      const dayEndMs = moment(selectedDateStr).endOf('day').valueOf();
      const minTime = dayStartMs;
      const maxTime = dayEndMs;
      const totalDuration = maxTime - minTime;
      const toX = (ts) => ((ts - minTime) / totalDuration) * CHART_W;
      const laneCenter = (idx) => TOP_PAD + idx * LANE_H + LANE_H / 2;
      const polylinePoints = [];
      allSegments.forEach((seg, i) => {
        const lane = LANES[seg.type] || LANES[241];
        const y = laneCenter(lane.idx);
        const x1 = toX(seg.start);
        const x2 = toX(seg.end);
        polylinePoints.push(`${x1.toFixed(1)},${y.toFixed(1)}`);
        polylinePoints.push(`${x2.toFixed(1)},${y.toFixed(1)}`);
        if (i < allSegments.length - 1) {
          const nextLane = LANES[allSegments[i + 1].type] || LANES[241];
          const nextY = laneCenter(nextLane.idx);
          polylinePoints.push(`${x2.toFixed(1)},${nextY.toFixed(1)}`);
        }
      });
      const segmentRects = allSegments.map((seg, i) => {
        const lane = LANES[seg.type] || LANES[241];
        const x1 = toX(seg.start);
        const x2 = toX(seg.end);
        return {
          key: i,
          x: x1,
          y: TOP_PAD + lane.idx * LANE_H + 8,
          w: Math.max(x2 - x1, 1.5),
          h: LANE_H - 16,
          color: lane.color,
          type: seg.type,
        };
      });
      const hourTicks = [];
      for (let h = 0; h <= 24; h += 3) {
        hourTicks.push(dayStartMs + h * 3600000);
      }
      const visibleTicks = hourTicks;
      const byType = {};
      allSegments.forEach(seg => { byType[seg.type] = (byType[seg.type] || 0) + seg.len; });
      const totalSec = Object.values(byType).reduce((s, v) => s + v, 0);
      const fmtTime = (sec) => {
        const h = Math.floor(sec / 3600);
        const m = Math.floor((sec % 3600) / 60);
        return h > 0 ? `${h}h ${m}m` : `${m}m`;
      };
      return (
        <Box sx={{ width: '100%', bgcolor: '#0D1B2A', borderRadius: '12px', p: 1.5, overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', gap: 2.5, pb: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            {LANE_ORDER.map(type => {
              const lane = LANES[type];
              const sec = byType[type] || 0;
              const pct = totalSec > 0 ? Math.round((sec / totalSec) * 100) : 0;
              return (
                <Box key={type} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: lane.color }} />
                    <Typography sx={{ color: '#aaa', fontSize: '11px' }}>{lane.label}</Typography>
                  </Box>
                  <Typography sx={{ color: '#fff', fontSize: '11px', fontWeight: 700 }}>{pct}%</Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '10px' }}>{fmtTime(sec)}</Typography>
                </Box>
              );
            })}
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
              <Typography sx={{ color: '#fff', fontSize: '12px', fontWeight: 700 }}>
                Total: {fmtTime(totalSec)}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', width: '100%' }}>
            <Box sx={{ flexShrink: 0, width: `${LABEL_W}px` }}>
              <svg
                width={LABEL_W}
                height={SVG_H}
                style={{ display: 'block' }}
              >
                <rect x="0" y="0" width={LABEL_W} height={SVG_H} fill="#0D1B2A" />
                {LANE_ORDER.map((type, i) => {
                  const lane = LANES[type];
                  return (
                    <text
                      key={type}
                      x={LABEL_W - 6}
                      y={TOP_PAD + i * LANE_H + LANE_H / 2}
                      textAnchor="end"
                      dominantBaseline="central"
                      fill={lane.color}
                      fontSize="12"
                      fontFamily="sans-serif"
                      fontWeight="600"
                    >
                      {lane.label}
                    </text>
                  );
                })}
                <rect x="0" y={TOP_PAD + CHART_H} width={LABEL_W} height={BOTTOM_PAD} fill="#0D1B2A" />
              </svg>
            </Box>
            <Box
              ref={(el) => {
                if (el && allSegments.length > 0) {
                  const sleepStartRatio = (allSegments[0].start - minTime) / totalDuration;
                  const sleepStartPx = sleepStartRatio * CHART_W;
                  el.scrollLeft = Math.max(0, sleepStartPx - 80);
                }
              }}
              sx={{
                flex: 1,
                overflowX: 'auto',
                overflowY: 'hidden',
                '&::-webkit-scrollbar': { height: '4px' },
                '&::-webkit-scrollbar-track': {
                  background: 'rgba(255,255,255,0.05)',
                  borderRadius: '2px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: 'rgba(125,211,248,0.4)',
                  borderRadius: '2px',
                },
              }}
            >
              <svg
                width={CHART_W}
                height={SVG_H}
                style={{ display: 'block', minWidth: `${CHART_W}px` }}
              >
                <rect x="0" y="0" width={CHART_W} height={SVG_H} fill="#0D1B2A" />
                {LANE_ORDER.map((type, i) => (
                  <rect
                    key={type}
                    x={0} y={TOP_PAD + i * LANE_H}
                    width={CHART_W} height={LANE_H}
                    fill={i % 2 === 0 ? 'rgba(255,255,255,0.025)' : 'rgba(255,255,255,0.012)'}
                  />
                ))}
                {visibleTicks.map((t, i) => {
                  const x = toX(t);
                  return (
                    <line
                      key={i}
                      x1={x} y1={TOP_PAD}
                      x2={x} y2={TOP_PAD + CHART_H}
                      stroke="rgba(255,255,255,0.08)"
                      strokeWidth="1"
                    />
                  );
                })}
                {[1, 2, 3].map(i => (
                  <line
                    key={i}
                    x1={0} y1={TOP_PAD + i * LANE_H}
                    x2={CHART_W} y2={TOP_PAD + i * LANE_H}
                    stroke="rgba(255,255,255,0.07)"
                    strokeWidth="1"
                  />
                ))}
                {allSegments.map((seg, i) => {
                  const lane = LANES[seg.type] || LANES[241];
                  const x1 = toX(seg.start);
                  const x2 = toX(seg.end);
                  const lineY = laneCenter(lane.idx);
                  const baseY = TOP_PAD + CHART_H;
                  return (
                    <path
                      key={i}
                      d={`M${x1},${baseY} L${x1},${lineY} L${x2},${lineY} L${x2},${baseY} Z`}
                      fill={lane.color}
                      opacity={seg.type === 244 ? 0.25 : 0.18}
                    />
                  );
                })}

                <polyline
                  points={polylinePoints.join(' ')}
                  fill="none"
                  stroke="#7DD3F8"
                  strokeWidth="6"
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  opacity="0.12"
                />
                <polyline
                  points={polylinePoints.join(' ')}
                  fill="none"
                  stroke="#7DD3F8"
                  strokeWidth="2.2"
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  opacity="1"
                />
                <line
                  x1={0} y1={TOP_PAD + CHART_H}
                  x2={CHART_W} y2={TOP_PAD + CHART_H}
                  stroke="rgba(255,255,255,0.15)"
                  strokeWidth="1"
                />
                {visibleTicks.map((t, i) => {
                  const x = toX(t);
                  const d = new Date(t);
                  const h = d.getHours();
                  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
                  const ampm = h >= 12 ? 'PM' : 'AM';
                  const label = h === 0 ? '12 AM' : h === 12 ? '12 PM' : `${h12} ${ampm}`;
                  const isFirst = i === 0;
                  const isLast = i === visibleTicks.length - 1;
                  return (
                    <g key={i}>
                      <line
                        x1={x} y1={TOP_PAD + CHART_H}
                        x2={x} y2={TOP_PAD + CHART_H + 6}
                        stroke="rgba(255,255,255,0.25)"
                        strokeWidth="1"
                      />
                      <text
                        x={x}
                        y={TOP_PAD + CHART_H + 18}
                        textAnchor={isFirst ? 'start' : isLast ? 'end' : 'middle'}
                        dominantBaseline="hanging"
                        fill="rgba(255,255,255,0.55)"
                        fontSize="11"
                        fontFamily="sans-serif"
                      >
                        {label}
                      </text>
                    </g>
                  );
                })}
                <text
                  x={toX(allSegments[0].start)}
                  y={TOP_PAD + CHART_H + 36}
                  textAnchor="middle"
                  dominantBaseline="hanging"
                  fill="rgba(255,255,255,0.35)"
                  fontSize="10"
                  fontFamily="sans-serif"
                >
                  {moment(allSegments[0].start).format('h:mm A')}
                </text>
                <text
                  x={toX(allSegments[allSegments.length - 1].end)}
                  y={TOP_PAD + CHART_H + 36}
                  textAnchor="middle"
                  dominantBaseline="hanging"
                  fill="rgba(255,255,255,0.35)"
                  fontSize="10"
                  fontFamily="sans-serif"
                >
                  {moment(allSegments[allSegments.length - 1].end).format('h:mm A')}
                </text>

              </svg>
            </Box>
          </Box>
        </Box>
      );
    }
    if (currentDur === 'd') {
      const selectedDateStr = targetMoment.format('YYYY-MM-DD');

      const hourlySlots = Array.from({ length: 24 }, (_, i) => ({
        name: `${i}`,
        deep: 0,
        rem: 0,
        light: 0,
      }));

      const dayRecords = data.filter(r =>
        getRecordDate(r).format('YYYY-MM-DD') === selectedDateStr
      );

      dayRecords.forEach(record => {
        (record.sleepData || []).forEach(seg => {
          if (seg.sleepType === 244) return;
          const hour = moment(seg.sleepStartTime).hour();
          if (hour >= 0 && hour < 24) {
            const hrs = +(seg.sleepLen / 3600).toFixed(2);
            if (seg.sleepType === 243) {
              hourlySlots[hour].deep += hrs;
            } else if (seg.sleepType === 241) {
              hourlySlots[hour].rem += hrs;
            } else {
              hourlySlots[hour].light += hrs;
            }
          }
        });
      });

      if (dayRecords.length === 0) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 220 }}>
            <Typography variant="caption" color="textSecondary">
              No sleep data for this day
            </Typography>
          </Box>
        );
      }
      return (
        <Box sx={{ width: '100%' }}>
          <Box sx={{ width: '100%', height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={hourlySlots} barCategoryGap="20%" barGap={2} margin={{ top: 0, right: 5, left: 15, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E0E0E0" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#888' }} interval={3} />
                <YAxis orientation="right" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#888' }} tickFormatter={v => `${v}h`} width={35} />
                <RechartsTooltip
                  formatter={(value, name) => {
                    const totalMin = Math.round(Number(value) * 60);
                    const h = Math.floor(totalMin / 60);
                    const m = totalMin % 60;
                    const formatted = h > 0 && m > 0 ? `${h}h ${m}m` : h > 0 ? `${h}h` : `${m}m`;
                    return [formatted, name === 'deep' ? '🌑 Deep' : name === 'rem' ? '💤 REM' : '🌤 Light'];
                  }}
                  labelFormatter={(label) => `Hour: ${label}:00`}
                  contentStyle={{ fontSize: 12, borderRadius: 8 }}
                />
                <Bar dataKey="deep" name="deep" fill="#1A3A6B" radius={[0, 0, 0, 0]} barSize={14} stackId="sleep" isAnimationActive={false} />
                <Bar dataKey="rem" name="rem" fill="#26C6DA" radius={[0, 0, 0, 0]} barSize={14} stackId="sleep" isAnimationActive={false} />
                <Bar dataKey="light" name="light" fill="#4A90D9" radius={[4, 4, 0, 0]} barSize={14} stackId="sleep" isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      );
    }
    let dailyData = [];
    if (currentDur === 'w') {
      const startOfWeek = moment(targetMoment).startOf('isoWeek');
      const weekSlots = Array.from({ length: 7 }, (_, i) => {
        const day = moment(startOfWeek).add(i, 'days');
        return { dateStr: day.format('YYYY-MM-DD'), name: ['M', 'T', 'W', 'T', 'F', 'S', 'S'][i], deepSec: 0, remSec: 0, lightSec: 0 };
      });
      const weekStart = moment(targetMoment).startOf('isoWeek');
      const weekEnd = moment(targetMoment).endOf('isoWeek');
      const weekRecords = data.filter(r => {
        const rd = getRecordDate(r);
        return rd.isBetween(weekStart, weekEnd, null, '[]');
      });
      weekRecords.forEach(record => {
        weekSlots.forEach(slot => {
          const { deepSec, remSec, lightSec } = getSleepSecsByType(record, slot.dateStr);
          slot.deepSec += deepSec;
          slot.remSec += remSec;
          slot.lightSec += lightSec;
        });
      });
      dailyData = weekSlots.map(({ name, deepSec, remSec, lightSec }) => ({
        name,
        deep: +(deepSec / 3600).toFixed(1),
        rem: +(remSec / 3600).toFixed(1),
        light: +(lightSec / 3600).toFixed(1),
      }));
    } else if (currentDur === 'm') {
      const daysInMonth = targetMoment.daysInMonth();
      const startOfMonth = moment(targetMoment).startOf('month');
      const monthSlots = Array.from({ length: daysInMonth }, (_, i) => {
        const day = moment(startOfMonth).add(i, 'days');
        return { dateStr: day.format('YYYY-MM-DD'), name: day.format('D'), deepSec: 0, remSec: 0, lightSec: 0 };
      });
      const monthRecords = data.filter(r => getRecordDate(r).isSame(targetMoment, 'month'));
      monthRecords.forEach(record => {
        monthSlots.forEach(slot => {
          const { deepSec, remSec, lightSec } = getSleepSecsByType(record, slot.dateStr);
          slot.deepSec += deepSec;
          slot.remSec += remSec;
          slot.lightSec += lightSec;
        });
      });
      dailyData = monthSlots.map(({ name, deepSec, remSec, lightSec }) => ({
        name,
        deep: +(deepSec / 3600).toFixed(1),
        rem: +(remSec / 3600).toFixed(1),
        light: +(lightSec / 3600).toFixed(1),
      }));
    } else if (currentDur === 'y') {
      const yAxisLabels = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];
      const targetYear = targetMoment.year();
      const yearSlots = yAxisLabels.map((name, idx) => ({ name, monthIdx: idx, deepSec: 0, remSec: 0, lightSec: 0 }));
      const yearRecords = data.filter(r => getRecordDate(r).year() === targetYear);
      yearRecords.forEach(record => {
        const slot = yearSlots[getRecordDate(record).month()];
        if (!slot) return;
        const { deepSec, remSec, lightSec } = getSleepSecsByType(record, getRecordDate(record).format('YYYY-MM-DD'));
        slot.deepSec += deepSec;
        slot.remSec += remSec;
        slot.lightSec += lightSec;
      });
      dailyData = yearSlots.map(({ name, deepSec, remSec, lightSec }) => ({
        name,
        deep: +(deepSec / 3600).toFixed(1),
        rem: +(remSec / 3600).toFixed(1),
        light: +(lightSec / 3600).toFixed(1),
      }));
    }

    return (
      <Box sx={{ width: '100%', mt: 0, height: '100%' }}>
        <Box sx={{ width: '100%', height: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={dailyData} barCategoryGap="30%" barGap={0} margin={{ top: 0, right: 5, left: 15, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E0E0E0" />
              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#888' }} interval={currentDur === 'm' ? 4 : 0} />
              <YAxis orientation="right" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#888' }} tickFormatter={v => `${v}h`} width={35} />
              <RechartsTooltip
                formatter={(value, name) => {
                  const totalMin = Math.round(Number(value) * 60);
                  const h = Math.floor(totalMin / 60);
                  const m = totalMin % 60;
                  const formatted = h > 0 && m > 0 ? `${h}h ${m}m` : h > 0 ? `${h}h` : `${m}m`;
                  return [formatted, name === 'deep' ? '🌑 Deep' : name === 'rem' ? '💤 REM' : '🌤 Light'];
                }}
                contentStyle={{ fontSize: 12, borderRadius: 8 }}
              />
              <Bar dataKey="deep" name="deep" fill="#1A3A6B" radius={[0, 0, 0, 0]} barSize={18} stackId="sleep" isAnimationActive={false} />
              <Bar dataKey="rem" name="rem" fill="#26C6DA" radius={[0, 0, 0, 0]} barSize={18} stackId="sleep" isAnimationActive={false} />
              <Bar dataKey="light" name="light" fill="#4A90D9" radius={[4, 4, 0, 0]} barSize={18} stackId="sleep" isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    );
  };

  const getHealthParamType = (primaryKey) => {
    const typeMap = {
      'bloodSugarData': 'blood-sugar',
      'bloodPressureData': 'blood-pressure',
      'bodyTemperatureData': 'body-temperature',
      'heartRateData': 'heart-rate',
      'lipidData': 'lipid',
      'respiratorieData': 'respiratory-rate',
      'spo2sData': 'spo2',
      'stepsData': 'steps'
    };
    return typeMap[primaryKey] || primaryKey;
  };


  // Delete handler function
  const handleDelete = React.useCallback(async () => {
    if (isDeleting) return;

    setIsDeleting(true);
    setDeleteDialogOpen(false);

    try {
      console.log(`🗑️ Deleting data for: ${item.primaryKey}`);

      // Prepare delete payload following your project pattern
      const deletePayload = {
        token: userData.token,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
        duration: duration[item.primaryKey] || 'w',
        date: date[item.primaryKey] || new Date().toISOString().split('T')[0],
        type: getHealthParamType(item.primaryKey)
      };

      // Add patient/appointment ID based on user type
      if (userType === 'doctor') {
        deletePayload.appointmentId = item._id || '';
      } else if (userType === 'organization' || userType === 'subadmin') {
        deletePayload.patientId = item.patientId || '';
      }

      // Dispatch delete action using Redux pattern
      dispatch(deleteHealthParams(deletePayload));

      console.log(`✅ Delete action dispatched for: ${item.primaryKey}`);

      // Reset loading state after a short delay
      setTimeout(() => {
        setIsDeleting(false);
      }, 2000);

    } catch (error) {
      console.error(`❌ Failed to delete ${item.primaryKey} data:`, error);
      setIsDeleting(false);
    }
  }, [isDeleting, userData, duration, date, item, userType, dispatch]);

  // Helper function to format date range for AI modal
  const getFormattedDateRange = (durationValue, dateValue) => {
    const selectedDate = new Date(dateValue);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    if (durationValue === 'm') {
      return `${months[selectedDate.getMonth()]} ${selectedDate.getFullYear()}`;
    }

    if (durationValue === 'y') {
      return selectedDate.getFullYear().toString();
    }

    if (durationValue === 'w') {
      const day = selectedDate.getDay();
      const oneDayValue = 1000 * 60 * 60 * 24;
      let lower = selectedDate;
      let higher = selectedDate;

      if (day > 1) {
        lower = new Date(selectedDate.getTime() - oneDayValue * (day - 1));
      }
      if (day < 7 && day > 0) {
        higher = new Date(selectedDate.getTime() + oneDayValue * (7 - day));
      }
      if (day === 0) {
        lower = new Date(selectedDate.getTime() - oneDayValue * 6);
      }

      const lowerYear = lower.getFullYear();
      const higherYear = higher.getFullYear();

      if (lowerYear === higherYear) {
        return `${lower.getDate()} ${months[lower.getMonth()]} - ${higher.getDate()} ${months[higher.getMonth()]} ${lowerYear}`;
      } else {
        return `${lower.getDate()} ${months[lower.getMonth()]} ${lowerYear} - ${higher.getDate()} ${months[higher.getMonth()]} ${higherYear}`;
      }
    }

    return dateValue;
  };

  // AI Analysis handler function - Uses aiAnalysisService with caching
  const handleAIAnalysis = React.useCallback(async () => {
    try {
      // Open modal immediately
      setAiModalOpen(true);

      const userType = localStorage.getItem("userType");
      const durationValue = (duration[item.primaryKey] || 'w').toLowerCase();
      const dateValue = date[item.primaryKey] || new Date().toISOString().split('T')[0];

      // Create cache key for this specific AI analysis request
      const cacheKey = `ai_${item.primaryKey}_${userType}_${durationValue}_${dateValue}_${patientID || item._id || ''}`;

      // Helper to safely stringify any non-string values
      const safeStringify = (val) => {
        if (!val) return null;
        if (typeof val === 'string') return val;
        if (Array.isArray(val)) return val.join('. ');
        if (typeof val === 'object') {
          return Object.values(val)
            .filter(v => typeof v === 'string')
            .join('. ') || JSON.stringify(val);
        }
        return String(val);
      };

      // Check if we have cached AI data for this exact request
      const cachedAiData = localStorage.getItem(cacheKey);
      if (cachedAiData) {
        try {
          let parsedData = JSON.parse(cachedAiData);
          // Apply safety filters in case cached data was saved before the stringify fix
          parsedData = {
            ...parsedData,
            explanation: safeStringify(parsedData.explanation),
            trends: safeStringify(parsedData.trends),
            doctor_consultation: safeStringify(parsedData.doctor_consultation),
            lab_report_note: safeStringify(parsedData.lab_report_note)
          };
          setAiData(parsedData);
          return;
        } catch (e) {
          localStorage.removeItem(cacheKey);
        }
      }

      // Build payload for the service
      const payload = {
        parameterKey: item.primaryKey,
        token: userData.token,
        duration: durationValue,
        date: dateValue,
      };

      // Add context IDs based on user type
      if (userType === 'doctor') {
        payload.appointmentId = item._id || '';
      } else if (userType === 'organization' || userType === 'subadmin') {
        payload.patientId = patientID || item.patientId || item.patientID || '';
      }

      const apiResponse = await getParameterAIAnalysis(payload);

      console.log('AI apiResponse:', JSON.stringify(apiResponse?.response?.ai_data));

      if (apiResponse.response_type === 'success' && apiResponse.response?.ai_data) {
        const aiData = apiResponse.response.ai_data;

        // ── Risk / Status level ────────────────────────────────────────────
        // Different params use different field names for the risk level
        const risk_level =
          aiData.risk_level ||
          aiData.oxygen_saturation_status ||
          aiData.blood_sugar_status ||
          aiData.blood_pressure_status ||
          aiData.heart_rate_status ||
          aiData.respiratory_rate_status ||
          aiData.temperature_status ||
          aiData.status ||
          'unknown';

        // ── Explanation / Analysis text ────────────────────────────────────
        const explanation =
          aiData.trend_explanation ||
          aiData.explanation ||
          aiData.trend_analysis ||
          aiData.analysis ||
          aiData.summary_text ||
          (aiData.summary ? JSON.stringify(aiData.summary) : null) ||
          'No analysis available';

        // ── Trends / Monitoring text ───────────────────────────────────────
        let trendsText =
          aiData.monitoring_advice ||
          aiData.trends ||
          aiData.trend_analysis ||
          (aiData.recommendations?.monitoring
            ? (Array.isArray(aiData.recommendations.monitoring)
              ? aiData.recommendations.monitoring.join(' ')
              : aiData.recommendations.monitoring)
            : null) ||
          'No trend data available';
        if (Array.isArray(trendsText)) {
          trendsText = trendsText.join('. ');
        }

        // ── Lifestyle advice array ─────────────────────────────────────────
        let adviceArray =
          aiData.lifestyle_advice ||
          aiData.advice ||
          aiData.recommendations?.lifestyle ||
          aiData.recommendations?.advice ||
          [];
        if (typeof adviceArray === 'string') {
          adviceArray = [adviceArray];
        }
        if (!Array.isArray(adviceArray)) {
          adviceArray = [];
        }

        // Ensure every item in adviceArray is actually a string, not an object nested inside an array
        adviceArray = adviceArray.map(item => safeStringify(item));

        // ── Doctor consultation ────────────────────────────────────────────
        let doctor_consultation =
          aiData.doctor_consultation ||
          aiData.recommendations?.when_to_consult_doctor ||
          null;
        if (Array.isArray(doctor_consultation)) {
          doctor_consultation = doctor_consultation.join(' ');
        }


        // Map API response fields to expected modal fields
        const mappedAiData = {
          risk_level: typeof risk_level === 'string' ? risk_level : 'unknown',
          explanation: safeStringify(explanation) || 'No analysis available',
          trends: safeStringify(trendsText) || 'No trend data available',
          advice: adviceArray,
          doctor_consultation: safeStringify(doctor_consultation),
          dateRange: getFormattedDateRange(durationValue, dateValue)
        };

        // Cache the AI data for future use
        localStorage.setItem(cacheKey, JSON.stringify(mappedAiData));

        setAiData(mappedAiData);
      }
    } catch (error) {
      console.error(`Failed to get AI analysis for ${item.primaryKey}:`, error);
    }
  }, [userData, duration, date, item, userType, patientID, getFormattedDateRange]);



  const getDuration = (item, _item) => {
    setDuration(prev => ({ ...prev, [item.primaryKey]: _item }))
  }

  // Function to refresh data from server (bypass cache for specific graph only)
  const handleRefresh = React.useCallback(async () => {
    if (isRefreshing) return;

    setIsRefreshing(true);

    try {
      const { invalidateByPattern } = require('../../../redux/cache/cacheSlice');

      // Create a specific pattern for this health parameter type and current settings
      const paramTypePattern = getHealthParameterCachePattern(item.primaryKey, {
        userType,
        duration: duration[item.primaryKey],
        date: date[item.primaryKey]
      });

      // Clear cache entries that match this specific health parameter pattern
      dispatch(invalidateByPattern({
        cacheType: 'healthParams',
        pattern: paramTypePattern
      }));

      // Create payload for API request
      const payload = {
        token: userData.token,
        duration: duration[item.primaryKey] || 'w',
        date: date[item.primaryKey] || new Date().toISOString().split('T')[0]
      };

      // Add appointment/patient ID based on user type
      if (userType === 'doctor') {
        payload.appointmentId = item._id || '';
      } else if (userType === 'organization' || userType === 'subadmin') {
        payload.patientId = patientID || item.patientId || item.patientID || '';
      }
      // Dispatch appropriate action based on health parameter type
      switch (item.primaryKey) {
        case 'bloodSugarData':
          dispatch(bloodSugar(payload));
          break;
        case 'bloodPressureData':
          dispatch(bloodPressure(payload));
          break;
        case 'spo2sData':
          dispatch(bloodOxygen(payload));
          break;
        case 'bodyTemperatureData':
          dispatch(bodyTemp(payload));
          break;
        case 'heartRateData':
          dispatch(heartRate(payload));
          break;
        case 'respiratorieData':
          dispatch(respiratoryRate(payload));
          break;
        case 'stepsData':                      
          dispatch(getStepsData(payload));     
          break;
        default:
          dispatch(healthParams(payload));
          break;
      }

      console.log(`✅ Refreshed data for: ${item.primaryKey}`);

      // Show success feedback
      setTimeout(() => {
        setIsRefreshing(false);
      }, 1000);

    } catch (error) {
      console.error(`❌ Failed to refresh ${item.primaryKey} data:`, error);
      setIsRefreshing(false);
    }
  }, [isRefreshing, userData, duration, date, item, userType, dispatch]);

  // Helper function to create cache pattern for specific health parameter
  const getHealthParameterCachePattern = (paramKey, options) => {
    // Map primary keys to their API endpoint patterns
    const paramTypeMap = {
      'bloodSugarData': 'bloodSugar',
      'bloodPressureData': 'bloodPressure',
      'spo2sData': 'bloodOxygen',
      'bodyTemperatureData': 'bodyTemp',
      'heartRateData': 'heartRate',
      'respiratorieData': 'respiratoryRate',
      'stepsData': 'steps'
    };

    const paramType = paramTypeMap[paramKey] || 'healthParams';

    // Create pattern that matches this specific parameter type
    return `"paramType":"${paramType}"`;
  };

  const getDateValue = React.useCallback((key, isMinMaxDate) => {
    const value = date[key]
    const dateValue = moment(value, 'YYYY-MM-DD')?._isValid ? new Date(moment(value, 'YYYY-MM-DD').format()) : new Date(value)
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', "Oct", 'Nov', 'Dec']
    if (duration[key].toLowerCase() === 'd') {
      if (isMinMaxDate) return [dateValue, dateValue]
      return `${dateValue.getDate()} ${months[dateValue.getMonth()]} ${dateValue.getFullYear()}`
    }
    if (duration[key].toLowerCase() === 'm') {
      return months[dateValue.getMonth()]
    }
    if (duration[key].toLowerCase() === 'y') {
      return dateValue.getFullYear()
    }
    if (duration[key].toLowerCase() === 'w') {
      const day = dateValue.getDay();
      const oneDayValue = 1000 * 60 * 60 * 24
      let lower = dateValue
      let higher = dateValue
      if (day > 1) {
        lower = new Date(dateValue.getTime() - oneDayValue * (day - 1))
      }
      if (day < 7 && day > 0) {
        higher = new Date(dateValue.getTime() + oneDayValue * (7 - day))
      }
      if (day == 0) {
        lower = new Date(dateValue.getTime() - oneDayValue * (6 - day))
      }
      if (isMinMaxDate) return [lower, higher]
      return `${lower.getDate()} ${months[lower.getMonth()]} - ${higher.getDate()} ${months[higher.getMonth()]}`
    }
    return '-'
  }, [date, duration])

  const handleClickArrow = React.useCallback((accumulator, key) => {
    const _dur = duration[key].toLowerCase()
    const aDay = 1000 * 60 * 60 * 24
    const value = date[key]
    const dateValue = moment(value, 'YYYY-MM-DD')?._isValid ? new Date(moment(value, 'YYYY-MM-DD').format()) : new Date(value)
    let formatValue = value
    const isLeap = dateValue.getFullYear % 4 === 0 && (dateValue.getFullYear() % 100 !== 0 || dateValue.getFullYear() % 100 === 0 && dateValue.getFullYear() % 400 === 0)
    if (_dur === 'y') {
      formatValue = dateValue.getTime() + aDay * (isLeap ? 365 : 364) * accumulator
    } else if (_dur === 'm') {
      const monthDays = [31, isLeap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]
      formatValue = dateValue.getTime() + aDay * monthDays[dateValue.getMonth()] * accumulator
    } else if (_dur === 'w') {
      formatValue = dateValue.getTime() + aDay * 7 * accumulator
    }else if (_dur === 'd') {
      formatValue = dateValue.getTime() + aDay * accumulator 
    }
    setDate(prev => ({ ...prev, [key]: moment(formatValue).format('YYYY-MM-DD') }))
  }, [duration, date])

  const formatGraphDataValues = React.useMemo(() => {
    if (item.graphType === 'stepsChart' || item.graphType === 'sleepChart') return [];
    return formatGraphData.call(healthParam[item.primaryKey], item.dataKey, duration[item.primaryKey], getDateValue(item.primaryKey, true), ['heartRateData', 'respiratorieData'].includes(item.primaryKey))
  }, [healthParam, duration])

    const stepsFormattedData = React.useMemo(() => {
    if (item.graphType !== 'stepsChart') return { graphData: [], totals: { steps: 0, calories: 0, distance: 0 } };
    
    const data = healthParam[item.primaryKey] || [];
    const selectedDate = moment(date[item.primaryKey] || new Date()).startOf('day');
    const currentDur = (duration[item.primaryKey] || 't').toLowerCase();

    let totals = { steps: 0, calories: 0, distance: 0 };
    let graphData = [];

    if (currentDur === 'd') {
      graphData = Array.from({ length: 24 }, (_, i) => ({
        name: `${i}:00`,
        step: 0, calorie: 0, distance: 0
      }));

      data.forEach(d => {
        const dDate = moment(d.start_time ? d.start_time * 1000 : d.createdAt); 
        
        if (dDate.isSame(selectedDate, 'day')) {
          const hour = dDate.hour();
          graphData[hour].step += (d.step || 0);
          graphData[hour].calorie += (d.calorie || 0);
          graphData[hour].distance += (d.distance || 0);
          
          totals.steps += (d.step || 0);
          totals.calories += (d.calorie || 0);
          totals.distance += (d.distance || 0);
        }
      });
    } else if (currentDur === 'w') {
      const day = selectedDate.day();
      const lower = moment(selectedDate).subtract(day === 0 ? 6 : day - 1, 'days');

      graphData = Array.from({ length: 7 }, (_, i) => {
        const d = moment(lower).add(i, 'days');
        return {
          name: d.format('D'),
          dateStr: d.format('YYYY-MM-DD'),
          step: 0, calorie: 0, distance: 0
        };
      });

      data.forEach(d => {
        const dDate = moment(d.start_time ? d.start_time * 1000 : d.createdAt);
        const match = graphData.find(g => g.dateStr === dDate.format('YYYY-MM-DD'));
        if (match) {
          match.step += (d.step || 0);
          match.calorie += (d.calorie || 0);
          match.distance += (d.distance || 0);

          totals.steps += (d.step || 0);
          totals.calories += (d.calorie || 0);
          totals.distance += (d.distance || 0);
        }
      });
    }else if (currentDur === 'm') {
      // DAILY VIEW (For "Month")
      const daysInMonth = selectedDate.daysInMonth();
      const startOfMonth = moment(selectedDate).startOf('month');

      graphData = Array.from({ length: daysInMonth }, (_, i) => {
        const d = moment(startOfMonth).add(i, 'days');
        return {
          name: d.format('D'),
          dateStr: d.format('YYYY-MM-DD'),
          step: 0, calorie: 0, distance: 0
        };
      });

      data.forEach(d => {
        const dDate = moment(d.start_time ? d.start_time * 1000 : d.createdAt);
        if (dDate.isSame(selectedDate, 'month')) {
          const match = graphData[dDate.date() - 1]; 
          if (match) {
            match.step += (d.step || 0);
            match.calorie += (d.calorie || 0);
            match.distance += (d.distance || 0);

            totals.steps += (d.step || 0);
            totals.calories += (d.calorie || 0);
            totals.distance += (d.distance || 0);
          }
        }
      });
    } else if (currentDur === 'y') {
      const months = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];
      
      graphData = Array.from({ length: 12 }, (_, i) => ({
        name: months[i],
        monthIndex: i,
        step: 0, calorie: 0, distance: 0
      }));

      data.forEach(d => {
        const dDate = moment(d.start_time ? d.start_time * 1000 : d.createdAt);
        if (dDate.isSame(selectedDate, 'year')) {
          const match = graphData[dDate.month()]; 
          if (match) {
            match.step += (d.step || 0);
            match.calorie += (d.calorie || 0);
            match.distance += (d.distance || 0);

            totals.steps += (d.step || 0);
            totals.calories += (d.calorie || 0);
            totals.distance += (d.distance || 0);
          }
        }
      });
    }
    return { graphData, totals };
  }, [healthParam, duration, date, item]);

  const calculateAvg = React.useCallback((index) => {

    if (item.graphType === 'stepsChart') {
      return stepsFormattedData.totals.steps;
    }
    if (item.graphType === 'sleepChart') {
      const sleepData = healthParam[item.primaryKey] || [];
      if (!Array.isArray(sleepData) || sleepData.length === 0) return '-';

      const currentDur = (duration[item.primaryKey] || 'w').toLowerCase();
      const targetMoment = moment(date[item.primaryKey]);

      const recDay = (r) => r.sleepDay ? moment(r.sleepDay) : moment(r.startTime * 1000);
      let matchingRecords = [];
      if (currentDur === 'd') {
        matchingRecords = sleepData.filter(r =>
          recDay(r).format('YYYY-MM-DD') === targetMoment.format('YYYY-MM-DD')
        );
      } else if (currentDur === 'w') {
        const start = moment(targetMoment).startOf('isoWeek');
        const end = moment(targetMoment).endOf('isoWeek');
        matchingRecords = sleepData.filter(r => recDay(r).isBetween(start, end, null, '[]'));
      } else if (currentDur === 'm') {
        matchingRecords = sleepData.filter(r => recDay(r).isSame(targetMoment, 'month'));
      } else if (currentDur === 'y') {
        matchingRecords = sleepData.filter(r => recDay(r).isSame(targetMoment, 'year'));
      }

      // total time asleep per record (prefer backend stageTotals; exclude Awake)
      const asleepOf = (r) => (r.stageTotals && r.stageTotals.asleepSec != null)
        ? r.stageTotals.asleepSec
        : (r.sleepData || []).filter(seg => seg.sleepType !== 244).reduce((s2, seg) => s2 + seg.sleepLen, 0);
      const totalSec = matchingRecords.reduce((sum, r) => sum + asleepOf(r), 0);

      // average over DISTINCT wake-up days (night + nap on one day count as one day)
      let displaySec = totalSec;
      if (currentDur !== 'd') {
        const days = new Set(matchingRecords.map(r => recDay(r).format('YYYY-MM-DD')));
        if (days.size > 0) displaySec = totalSec / days.size;
      }

      const h = Math.floor(displaySec / 3600);
      const m = Math.floor((displaySec % 3600) / 60);
      return `${h}h ${m}m`;
    }
    const isTopMost = ['bodyTemperatureData', 'bloodPressureData'].includes(item.primaryKey)
    const minMaxDate = getDateValue(item.primaryKey, true)
    const isRange = ['spo2sData', 'bloodPressureData'].includes(item.primaryKey)
    const isRangeAvg = ['respiratorieData', 'heartRateData'].includes(item.primaryKey)
    return getAvg.call(healthParam[item.primaryKey], item.dataKey.split('&')[index], isTopMost, duration[item.primaryKey], minMaxDate, isRange, formatGraphDataValues, isRangeAvg);
  }, [duration, healthParam, formatGraphDataValues])


  const DiabeleteRangeByCalculatons = React.useCallback(({ index }) => {
    const value = calculateAvg(index)
    if (!value || value === '-') return ''
    if (value < 100) {
      return <DiabetesRange type="1">Normal</DiabetesRange>
    } else if (value < 126) {
      return <DiabetesRange type="2">Prediabetes</DiabetesRange>
    } else {
      return <DiabetesRange type="3">Diabetes</DiabetesRange>
    }
  }, [calculateAvg])

  const handleExport = React.useCallback(async () => {
    if (isExporting) return;

    setIsExporting(true);

    try {

      const currentData = healthParam[item.primaryKey] || [];

      if (!currentData) {
        console.warn('currentData is null or undefined');
        setExportMessage('No data available to export.');
        setExportErrorModalOpen(true);
        setIsExporting(false);
        return;
      }

      if (!Array.isArray(currentData)) {
        console.warn('currentData is not an array:', typeof currentData);
        setExportMessage('Data format is incorrect for export.');
        setExportErrorModalOpen(true);
        setIsExporting(false);
        return;
      }

      if (currentData.length === 0) {
        console.warn('currentData array is empty');
        setExportMessage('No data available to export for the selected period.');
        setExportErrorModalOpen(true);
        setIsExporting(false);
        return;
      }

      const currentDuration = duration[item.primaryKey]?.toLowerCase() || 'w';
      const selectedDate = new Date(date[item.primaryKey]);
      const selectedYear = selectedDate.getFullYear();
      let processedData = currentData;

      if (currentDuration === 'y') {

        if (currentData[0] && typeof currentData[0] === 'object') {

          const firstItem = currentData[0];
          const hasMonthField = 'month' in firstItem || 'monthly' in firstItem;
          const hasYearField = 'year' in firstItem;
          const hasDateField = 'date' in firstItem;
        }
      }

      const exportData = processedData.map((dataPoint, index) => {

        let dateValue = dataPoint.date || dataPoint.createdAt || dataPoint.timestamp;
        let formattedDate = dateValue;
        if (currentDuration === 'y') {
          if (dataPoint.month && dataPoint.year) {
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            formattedDate = `${months[dataPoint.month - 1]} ${dataPoint.year}`;
          } else if (dataPoint.year) {
            formattedDate = dataPoint.year.toString();
          } else if (dataPoint.month) {
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            formattedDate = `${months[dataPoint.month - 1]} ${selectedYear}`;
          }
        } else if (currentDuration === 'm') {
          // 🟢 ADD: For monthly data, ensure year is included
          if (dateValue) {
            const dataDate = new Date(dateValue);
            if (!isNaN(dataDate.getTime())) {
              const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
              formattedDate = `${months[dataDate.getMonth()]} ${dataDate.getFullYear()}`;
            } else {
              // Fallback: use selected year
              formattedDate = `${dateValue} ${selectedYear}`;
            }
          } else {
            formattedDate = `Entry ${index + 1} (${selectedYear})`;
          }
        } else if (currentDuration === 'w') {
          // 🟢 ADD: For weekly data, include full date with year
          if (dateValue) {
            const dataDate = new Date(dateValue);
            if (!isNaN(dataDate.getTime())) {
              const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
              formattedDate = `${dataDate.getDate()} ${months[dataDate.getMonth()]} ${dataDate.getFullYear()}`;
            } else {
              // Fallback: use selected year
              formattedDate = `${dateValue} (${selectedYear})`;
            }
          } else {
            formattedDate = `Entry ${index + 1} (${selectedYear})`;
          }
        }
        const baseData = {
          'Date': dateValue || `Entry ${index + 1}`,
          'Parameter': item.primaryHeading,
          'Duration': currentDuration.toUpperCase(),
          'Selected Period': getDateValue(item.primaryKey),
          'Year': currentDuration === 'y' ? (dataPoint.year || selectedYear) : selectedYear
        };

        let specificData = {};

        switch (item.primaryKey) {
          case 'bloodPressureData':
            specificData = {
              'Systolic': dataPoint.sys_blood_pressure || dataPoint.systolic || dataPoint.sys || '-',
              'Diastolic': dataPoint.dia_blood_pressure || dataPoint.diastolic || dataPoint.dia || '-',
              'Unit': 'mmHg'
            };
            break;

          case 'bloodSugarData':
            specificData = {
              'Blood Sugar': dataPoint.blood_sugar || dataPoint.bloodSugar || dataPoint.glucose || dataPoint.value || '-',
              'Unit': 'mg/dL'
            };
            break;

          case 'heartRateData':
            specificData = {
              'Heart Rate': dataPoint.pulse || dataPoint.heartRate || dataPoint.bpm || dataPoint.value || '-',
              'Unit': 'BPM'
            };
            break;

          case 'bodyTemperatureData':
            specificData = {
              'Temperature': dataPoint.temperature || dataPoint.temp || dataPoint.value || '-',
              'Unit': item.unit || '°C'
            };
            break;

          case 'spo2sData':
            specificData = {
              'SPO2': dataPoint.percentage || dataPoint.spo2 || dataPoint.oxygen || dataPoint.value || '-',
              'Unit': '%'
            };
            break;

          case 'respiratorieData':
            specificData = {
              'Respiratory Rate': dataPoint.respiratory_rates || dataPoint.respiratoryRate || dataPoint.breathingRate || dataPoint.value || '-',
              'Unit': 'breaths/min'
            };
            break;

            case 'stepsData':
            specificData = {
              'Steps': dataPoint.step || '-',
              'Calories': dataPoint.calorie || '-',
              'Distance': dataPoint.distance || '-',
              'Unit': 'Steps'
            };
            break;

          default:
            break;
        }

        console.log('Specific data extracted:', specificData);

        return {
          ...baseData,
          ...specificData
        };
      });

      if (!exportData || exportData.length === 0) {
        console.error('Export data is empty after processing');
        alert('Failed to process data for export.');
        setIsExporting(false);
        return;
      }

      // Convert to CSV format
      const csvContent = convertToCSV(exportData);
      console.log('CSV content preview:', csvContent.substring(0, 200) + '...');

      if (!csvContent || csvContent.trim() === '') {
        console.error('CSV content is empty');
        alert('Failed to generate CSV content.');
        setIsExporting(false);
        return;
      }

      // Create and download the file with improved filename
      const periodText = getDateValue(item.primaryKey).toString().replace(/\s/g, '_').replace(/[^\w\-_]/g, '');
      const fileName = `${item.primaryHeading.replace(/\s/g, '_')}_${periodText}_${currentDuration.toUpperCase()}_${selectedYear}_${new Date().toISOString().split('T')[0]}.csv`;

      console.log('Generated filename:', fileName);

      downloadCSV(csvContent, fileName);

      console.log(`✅ Export completed for: ${item.primaryKey}`);

      // 🟢 CHANGE: Show success modal instead of alert
      setExportMessage(`Export completed successfully! File: ${fileName}`);
      setExportSuccessModalOpen(true);

      // Reset loading state
      setTimeout(() => {
        setIsExporting(false);
      }, 1000);

    } catch (error) {
      console.error(`❌ Failed to export ${item.primaryKey} data:`, error);
      console.error('Error stack:', error.stack);
      setExportMessage(`Export failed: ${error.message || 'Unknown error occurred'}`);
      setExportErrorModalOpen(true);
      setIsExporting(false);
    }
  }, [isExporting, healthParam, item, duration, getDateValue]);

  const convertToCSV = (data) => {
    console.log('Converting to CSV, data:', data);

    if (!data || !Array.isArray(data) || data.length === 0) {
      console.error('Invalid data for CSV conversion');
      return '';
    }

    try {
      const headers = Object.keys(data[0]);
      console.log('CSV headers:', headers);

      const csvRows = [
        headers.join(','),
        ...data.map((row, index) => {
          const csvRow = headers.map(header => {
            let value = row[header];

            if (value === null || value === undefined) {
              value = '-';
            }

            value = String(value);

            if (value.includes(',') || value.includes('"') || value.includes('\n')) {
              value = `"${value.replace(/"/g, '""')}"`;
            }

            return value;
          }).join(',');

          console.log(`CSV row ${index}:`, csvRow);
          return csvRow;
        })
      ];

      const csvContent = csvRows.join('\n');
      console.log('Final CSV content length:', csvContent.length);

      return csvContent;
    } catch (error) {
      console.error('Error in convertToCSV:', error);
      return '';
    }
  };

  const downloadCSV = (csvContent, fileName) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const getFormattedDateForModal = React.useCallback((key) => {
    const value = date[key];
    const dateValue = moment(value, 'YYYY-MM-DD')?._isValid ? new Date(moment(value, 'YYYY-MM-DD').format()) : new Date(value);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const currentDuration = duration[key].toLowerCase();

    if (currentDuration === 'm') {
      // 🟢 CHANGE: For monthly, show "Sep 2025" instead of just "Sep"
      return `${months[dateValue.getMonth()]} ${dateValue.getFullYear()}`;
    }

    if (currentDuration === 'y') {
      return dateValue.getFullYear().toString();
    }

    if (currentDuration === 'w') {
      // 🟢 CHANGE: For weekly, show full date range with year
      const day = dateValue.getDay();
      const oneDayValue = 1000 * 60 * 60 * 24;
      let lower = dateValue;
      let higher = dateValue;

      if (day > 1) {
        lower = new Date(dateValue.getTime() - oneDayValue * (day - 1));
      }
      if (day < 7 && day > 0) {
        higher = new Date(dateValue.getTime() + oneDayValue * (7 - day));
      }
      if (day == 0) {
        lower = new Date(dateValue.getTime() - oneDayValue * (6 - day));
      }

      // Show full date range with years
      const lowerYear = lower.getFullYear();
      const higherYear = higher.getFullYear();

      if (lowerYear === higherYear) {
        // Same year: "15 Sep - 21 Sep 2025"
        return `${lower.getDate()} ${months[lower.getMonth()]} - ${higher.getDate()} ${months[higher.getMonth()]} ${lowerYear}`;
      } else {
        // Different years: "29 Dec 2024 - 4 Jan 2025"
        return `${lower.getDate()} ${months[lower.getMonth()]} ${lowerYear} - ${higher.getDate()} ${months[higher.getMonth()]} ${higherYear}`;
      }
    }
    return '-';
  }, [date, duration]);

  const StepsCustomTooltip = ({ active, payload, label, duration }) => {
  if (active && payload && payload.length) {
    const data = payload[0]?.payload;
    const durationLabel = duration?.toLowerCase() === 'd' ? `Hour: ${label}` :
                          duration?.toLowerCase() === 'w' ? `Day: ${label}` :
                          duration?.toLowerCase() === 'm' ? `Date: ${label}` :
                          duration?.toLowerCase() === 'y' ? `Month: ${label}` : label;
    return (
      <Box sx={{
        background: '#fff',
        borderRadius: '8px',
        padding: '8px 12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        fontSize: '12px'
      }}>
        <div style={{ fontWeight: 600, marginBottom: 4, color: '#333' }}>{durationLabel}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#2AAB6C' }}>
          <DirectionsRunIcon sx={{ fontSize: 13 }} /> Steps: <strong>{data?.step || 0}</strong>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#FF7043' }}>
          <LocalFireDepartmentIcon sx={{ fontSize: 13 }} /> Calories: <strong>{data?.calorie || 0}</strong> kcal
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#42A5F5' }}>
          <RouteIcon sx={{ fontSize: 13 }} /> Distance: <strong>{data?.distance || 0}</strong> m
        </div>
      </Box>
    );
  }
  return null;
};
  return (
    <>
      <Paper className={className.healthInnerCont} width='260px' elevation={1}>
        <>
          <Box className={!!item.secondaryHeading ? className.listHeading : `${className.listHeading} ${className.lipidTextStyle}`} display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
            <span>{item.primaryHeading}</span>
            <Box display={'flex'} alignItems={'center'} gap={1}>
              {item.primaryKey === 'bloodSugarData' && <DiabeleteRangeByCalculatons index={0} />}

              {/* Export Button */}
              <Tooltip title="Export data to CSV" placement="top">
                <IconButton
                  size="small"
                  onClick={handleExport}
                  disabled={isExporting}
                  sx={{
                    padding: '1px',
                    color: '#2196F3',
                    '&:hover': {
                      backgroundColor: 'rgba(33, 150, 243, 0.1)',
                    },
                    '&.Mui-disabled': {
                      color: '#ccc',
                    },
                  }}
                >
                  <DownloadIcon
                    sx={{
                      fontSize: '16px',
                      transform: isExporting ? 'scale(0.8)' : 'scale(1)',
                      transition: 'transform 0.3s ease-in-out'
                    }}
                  />
                </IconButton>
              </Tooltip>
              <Tooltip title="Refresh data from server" placement="top">
                <IconButton
                  size="small"
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  sx={{
                    padding: '1px',
                    color: '#2AAB6C',
                    '&:hover': {
                      backgroundColor: 'rgba(42, 171, 108, 0.1)',
                    },
                    '&.Mui-disabled': {
                      color: '#ccc',
                    },
                  }}
                >
                  <RefreshIcon
                    sx={{
                      fontSize: '16px',
                      transform: isRefreshing ? 'rotate(360deg)' : 'none',
                      transition: 'transform 1s ease-in-out'
                    }}
                  />
                </IconButton>
              </Tooltip>

              {/* Delete Button */}
              <Tooltip title="Delete data for selected period" placement="top">
                <IconButton
                  size="small"
                  onClick={() => setDeleteDialogOpen(true)}
                  disabled={isDeleting}
                  sx={{
                    padding: '1px',
                    color: '#FF5252',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 82, 82, 0.1)',
                    },
                    '&.Mui-disabled': {
                      color: '#ccc',
                    },
                  }}
                >
                  <DeleteIcon
                    sx={{
                      fontSize: '16px',
                      transform: isDeleting ? 'scale(0.8)' : 'scale(1)',
                      transition: 'transform 0.3s ease-in-out'
                    }}
                  />
                </IconButton>
              </Tooltip>

              {/* AI Button - For all health parameters */}
              <Tooltip title="AI Analysis" placement="top">
                <IconButton
                  size="small"
                  onClick={handleAIAnalysis}
                  sx={{
                    padding: '1px',
                    color: '#9C27B0',
                    '&:hover': {
                      backgroundColor: 'rgba(156, 39, 176, 0.1)',
                    },
                  }}
                >
                  <PsychologyIcon
                    sx={{
                      fontSize: '16px',
                    }}
                  />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Box sx={{ position: 'relative' }}>
            {isCardLoading && (
              <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 10,
                backgroundColor: 'rgba(255,255,255,0.88)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                borderRadius: '0 0 8px 8px',
              }}>
                <Skeleton variant="rectangular" width="65%" height={10} sx={{ borderRadius: 5 }} />
                <Skeleton variant="rectangular" width="45%" height={10} sx={{ borderRadius: 5 }} />
                <Box sx={{ display: 'flex', gap: '7px', alignItems: 'flex-end', mt: 1 }}>
                  {[35, 60, 85, 50, 75, 55, 40].map((h, i) => (
                    <Skeleton key={i} variant="rectangular" width={24} height={h} sx={{ borderRadius: '4px 4px 0 0' }} />
                  ))}
                </Box>
              </Box>
            )}
          <Grid container className={className.secondaryCont}>
            <Grid item xs={10} className={`${className.secondaryHeadingStyle} ${className.multipleSecondaryValue}`}>
              {item.secondaryHeading.split('&').map((_item, index) => (
                <Grid container key={_item}>
                  {item.graphType === 'sleepChart' && index === 0 ? (
                    <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span>
                        {item.graphType === 'sleepChart' && duration[item.primaryKey]?.toLowerCase() !== 'd'
                          ? `Avg. ${_item.replace('Weekly', headingText[duration[item.primaryKey].toLowerCase()] || 'Weekly')}`
                          : _item.replace('Weekly', headingText[duration[item.primaryKey].toLowerCase()] || 'Weekly')
                        }
                      </span>
                      {duration[item.primaryKey]?.toLowerCase() === 'd' && (
                        <Tooltip title="View sleep stages" placement="top">
                          <Box
                            onClick={() => setSleepExpandOpen(true)}
                            sx={{
                              display: 'flex', alignItems: 'center', gap: '3px', cursor: 'pointer',
                              background: 'linear-gradient(135deg, #1A3A6B 0%, #4A90D9 100%)',
                              borderRadius: '20px', px: '8px', py: '3px',
                              animation: 'sleepPulse 2.5s ease-in-out infinite',
                              '@keyframes sleepPulse': {
                                '0%,100%': { boxShadow: '0 0 0 0 rgba(74,144,217,0.5)' },
                                '50%': { boxShadow: '0 0 0 6px rgba(74,144,217,0)' },
                              },
                              '&:hover': { background: 'linear-gradient(135deg,#26C6DA 0%,#1A3A6B 100%)', transform: 'scale(1.05)' },
                              transition: 'all 0.2s ease',
                            }}
                          >
                            <OpenInFullIcon sx={{ fontSize: 11, color: '#fff' }} />
                            <Typography sx={{ fontSize: '10px', color: '#fff', fontWeight: 700, lineHeight: 1 }}>Stages</Typography>
                          </Box>
                        </Tooltip>
                      )}
                    </Grid>
                  ) : (
  <Grid item xs={12}>{_item.replace('Weekly', headingText[duration[item.primaryKey].toLowerCase()] || 'Weekly')}</Grid>
)}
                  <Grid item xs={12} className='averageUnit'>
                    {item.primaryKey === 'bloodPressureData' ?
                      (() => {
                        const data = JSON.parse(JSON.stringify(healthParam[item.primaryKey]));
                        if (!data || !Array.isArray(data) || data.length === 0) return '-';
                        const key = item.dataKey.split('&')[index];
                        const avg = data.reduce((prev, curr) => prev + curr[key], 0) / data.length || 0;
                        return Math.round(avg);
                      })() : calculateAvg(index)
                    } {item.primaryKey === 'bodyTemperatureData'
                      ? _item?.toLowerCase() === 'fahrenheit' ? <>&#8457;</> : <>&#8451;</>
                      : item.graphType === 'sleepChart' ? '' : item.unit}
                  </Grid>
                </Grid>
              ))}
              {/* <Grid className='downloadIcon'><DownloadIcon /></Grid> */}
            </Grid>
            <Grid item xs={12}>
              <Grid container justifyContent="center">
                {(item.graphType === 'stepsChart' || item.graphType === 'sleepChart' ? ['D', 'W', 'M', 'Y'] : ['W', 'M', 'Y']).map((_item) => (
                  <Grid key={_item} item xs={2} sx={{ cursor: 'pointer' }} onClick={() => getDuration(item, _item)} className={`rangeSelectorDiv ${duration[item.primaryKey].toLowerCase() !== _item.toLowerCase() ? 'notSelectedRangeSelectorDiv' : ''}`}>
                    <Grid>{_item}</Grid>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <DateRangeGrid>
              {isOpen ? <DatePicker
                onChange={(e) => {
                  if (e) {
                    setDate(prev => ({ ...prev, [item.primaryKey]: moment(e).format('YYYY-MM-DD') }))
                  }
                }}
                format='YYYY-MM-DD'
                value={(moment(date[item.primaryKey], 'YYYY-MM-DD')?._isValid && new Date(moment(date[item.primaryKey], 'YYYY-MM-DD').format())) || ''}
                isOpen={isOpen}
                onCalendarClose={setIsOpen.bind(this, false)}
              /> :
                <Box sx={{
                  display: 'flex',
                  height: '30px',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  '& svg': {
                    width: '12px',
                    height: '12px',
                    fill: '#5E5E5E'
                  },
                  '& .dateRangeText': {
                    fontStyle: 'normal',
                    fontWeight: 400,
                    fontSize: '12px',
                    lineHeight: '16px',
                    color: '#5E5E5E',
                  }
                }}>
                  <IconButton disableRipple onClick={handleClickArrow.bind(null, -1, item.primaryKey)}>
                    <ArrowBackIosOutlined />
                  </IconButton>
                  <IconButton onClick={setIsOpen.bind(null, true)} disableRipple className='dateRangeText'>
                    {getDateValue(item.primaryKey)}
                  </IconButton>
                  <IconButton disableRipple onClick={handleClickArrow.bind(null, +1, item.primaryKey)}>
                    <ArrowForwardIosOutlined />
                  </IconButton>
                </Box>
              }
            </DateRangeGrid>
            {item.graphType === 'sleepChart' ? (
              <Box sx={{
                width: '100%',
              }}>
                <SleepChart
                  data={healthParam[item.primaryKey]}
                  date={date[item.primaryKey]}
                  duration={duration[item.primaryKey]}
                />
              </Box>
            ) :
item.graphType === 'stepsChart' ?
(
            <Box sx={{ width: '100%', mt: 0, height: '100%' }}>
    <Box sx={{ width: '100%', height: 220 }}>

      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={stepsFormattedData.graphData} margin={{ top: 0, right: 5, left: 15, bottom: 0 }}>
          
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E0E0E0" />
          
          <XAxis 
            dataKey="name" 
            axisLine={false} 
            tickLine={false} 
            tick={{fontSize: 11, fill: '#888'}} 
            interval={
              duration[item.primaryKey]?.toLowerCase() === 'd' ? 3 : 
              duration[item.primaryKey]?.toLowerCase() === 'm' ? 4 : 
              0
            } 
          />
          <YAxis 
            orientation="right" 
            axisLine={false} 
            tickLine={false} 
            tick={{fontSize: 11, fill: '#888'}} 
            width={35} 
          />
          
         <RechartsTooltip
  cursor={{ fill: 'rgba(0,0,0,0.04)' }}
  content={<StepsCustomTooltip duration={duration[item.primaryKey]} />}
/>
          
          <Bar 
            dataKey="step" 
            name="Steps" 
            fill="#2AAB6C" 
            radius={[10, 10, 10, 10]} 
            barSize={10} 
            // background={{ fill: '#F5F5F5', radius: 10 }} 
          />
        </BarChart>
      </ResponsiveContainer>
    </Box>

  </Box>
            ):
(
            !item.graphType ?
              <BarChart margin={{ top: 15, right: 0, bottom: 0, left: 0 }} width={275} height={220} data={formatGraphDataValues}>
                <XAxis dataKey="name" axisLine={false} tickLine={false} />
                <YAxis orientation="right" axisLine={false} tickLine={false} />
                <Bar dataKey="graphvalue" barSize={10} shape={customShape.bind(this, item.primaryKey)} />
                <defs>
                  <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                    {/* <stop offset="0%" stopColor="#097EB7" /> */}
                    <stop offset="100%" stopColor="#2AAB6C" />
                  </linearGradient>
                  <linearGradient id="colorUvBloodPressure" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="50%" stopColor="#FF5C5C" />
                    <stop offset="50%" stopColor="#3E8EDE" />
                  </linearGradient>
                </defs>
              </BarChart>
              :
              <Grid className={className.temperatureGraph}>
                <Grid>
                  <Stack>
                    <Stack>41 <span>&#8451;</span></Stack>
                    <Stack>High Fever</Stack>
                  </Stack>
                  <Stack>
                    <Stack>105.8 <span>&#8457;</span>
                    </Stack>
                  </Stack>
                </Grid>
                <Grid>
                  <Stack>
                    <Stack>39 <span>&#8451;</span></Stack>
                    <Stack>Mild Fever</Stack>
                  </Stack>
                  <Stack>
                    <Stack>102.2 <span>&#8457;</span>
                    </Stack>
                  </Stack>
                </Grid>
                <Grid sx={{ ...window.location.pathname !== "/patient/health" && { borderRadius: '0px 0px 8px 8px' } }}>
                  <Stack>
                    <Stack>38 <span>&#8451;</span></Stack>
                    <Stack>No Fever</Stack>
                  </Stack>
                  <Stack>
                    <Stack>
                      100.4 <span>&#8457;</span>
                    </Stack>
                  </Stack>
                </Grid>
                <Stack
                  sx={{
                    bottom: getBottomValueForPendulam(getAvg.call(healthParam[item.primaryKey], item.dataKey.split('&')[0], true)),
                    '& .vertical-lines': {
                      bottom: 2 - getBottomValueForPendulam(getAvg.call(healthParam[item.primaryKey], item.dataKey.split('&')[0], true)),
                      height: getBottomValueForPendulam(getAvg.call(healthParam[item.primaryKey], item.dataKey.split('&')[0], true)),
                    }
                  }}
                  className="temp-pendulam">
                  <Stack className='vertical-lines' />
                </Stack>
              </Grid>
)
            }
          </Grid>
          </Box>
    {window.location.pathname !== "/patient/dashboard" ?
  <Grid container spacing={0}
    onClick={() => {
      if (item.graphType !== 'stepsChart' && item.graphType !== 'sleepChart') {
        handleClick(item);
      }
    }}
  >
    {userType === 'patient' && (
      <Grid
        className={className.addMannualyButton}
        borderRadius={'0px 0px 4px 4px'}
        sx={
          (item.graphType === 'stepsChart' || item.graphType === 'sleepChart')
            ? {
                pointerEvents: 'none',
                cursor: 'default',
              }
            : {}
        }
      >
        <Stack className='mannualIcon'>
          {isHideModalPop || item.graphType === 'stepsChart' || item.graphType === 'sleepChart' ? null : <AddIcon />}
        </Stack>
        <Stack className="mannualText">
          {isHideModalPop || item.graphType === 'stepsChart' || item.graphType === 'sleepChart' ? null : "ADD MANUALLY"}
        </Stack>
      </Grid>
    )}
  </Grid>
  : null
}
        </>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete all {item.primaryHeading} data for the selected {
              duration[item.primaryKey].toLowerCase() === 'w' ? 'week' :
                duration[item.primaryKey].toLowerCase() === 'm' ? 'month' : 'year'
            }?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {/* 🟢 CHANGE: Use formatted date with year */}
            Selected period: {getFormattedDateForModal(item.primaryKey)}
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            This action cannot be undo.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <AIAnalysisModal
        open={aiModalOpen}
        onClose={() => {
          setAiModalOpen(false);
          setAiData(null); // Clear AI data when modal closes
        }}
        aiData={aiData}
        parameterName={item.primaryHeading}
      />

      {/* 🟢 ADD: Export Success Modal */}
      <ExportModal
        open={exportSuccessModalOpen}
        onClose={() => setExportSuccessModalOpen(false)}
        title="Export Successful"
        message={exportMessage}
        type="success"
      />

      {/* 🟢 ADD: Export Error Modal */}
      <ExportModal
        open={exportErrorModalOpen}
        onClose={() => setExportErrorModalOpen(false)}
        title="Export Failed"
        message={exportMessage}
        type="error"
      />
      {item.graphType === 'sleepChart' && (
        <Dialog
          open={sleepExpandOpen}
          onClose={() => setSleepExpandOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { bgcolor: '#0D1B2A', borderRadius: '16px', overflow: 'hidden' } }}
        >
          <DialogTitle sx={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            pb: 1, bgcolor: '#0D1B2A', color: '#fff',
            borderBottom: '1px solid rgba(255,255,255,0.08)'
          }}>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '16px', color: '#fff' }}>Sleep Stages</Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}>
                {date[item.primaryKey]}
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setSleepExpandOpen(false)} sx={{ color: 'rgba(255,255,255,0.5)' }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ bgcolor: '#0D1B2A', p: 2, minHeight: 380 }}>
            <SleepChart
              data={healthParam[item.primaryKey]}
              date={date[item.primaryKey]}
              duration={duration[item.primaryKey]}
              isExpanded
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}, (prevProps, nextProps) => {
  const key = prevProps.item.primaryKey;
  return (
    prevProps.item.primaryKey === nextProps.item.primaryKey &&
    prevProps.item._id === nextProps.item._id &&
    prevProps.duration[key] === nextProps.duration[key] &&
    prevProps.date[key] === nextProps.date[key] &&
    prevProps.className === nextProps.className &&
    prevProps.isHideModalPop === nextProps.isHideModalPop &&
    prevProps.patientID === nextProps.patientID
  );
})



export default function HealthParameters({ list, className, duration, setDuration, handleClick, date, setDate, isHideModalPop, patientID, item: parentItem }) {
  const healthParam = useSelector((state) => state?.user?.healthParam?.[0] || {});
  const dispatch = useDispatch();
  const userType = localStorage.getItem('userType');
  const token = useSelector((state) => state?.auth?.token);

  // ── SSE: receive ring health push and update redux immediately ────────────
  const handleRingUpdate = useCallback((event) => {
    if (!token) return;

    if (event.type === 'sync') {
      dispatch(healthParams({ token, bypassCache: true }));
    } else if (event.type === 'blood_pressure') {
      dispatch(bloodPressure({ token, bypassCache: true }));
    } else if (event.type === 'heart_rate') {
      dispatch(heartRate({ token, bypassCache: true }));
    } else if (event.type === 'spo2') {
      dispatch(bloodOxygen({ token, bypassCache: true }));
    } else if (event.type === 'temperature') {
      dispatch(bodyTemp({ token, bypassCache: true }));
    } else if (event.type === 'steps') {
      dispatch(getStepsData({ token, bypassCache: true }));
    } else if (event.type === 'sleep') {
      dispatch(getSleepData({ token, bypassCache: true }));
    }
  }, [dispatch, token]);

  // Only subscribe when patient is viewing their own dashboard
  useRingSSE(handleRingUpdate, userType === 'patient');
  // ─────────────────────────────────────────────────────────────────────────

  return (
    !!list && healthParam && Object.keys(healthParam).length > 0 ?
      list.map((item, index) => (
        healthParam[item.primaryKey] ?
          <Box key={item.primaryKey} minWidth='280px' marginBottom={window.location.pathname === "/patient/health" ? '15px' : '1px'} marginTop={'1px'}>
            <RenderList item={{ ...item, _id: parentItem?._id }} patientID={patientID} {...{ className, duration, setDuration, handleClick, date, setDate, isHideModalPop }} />
          </Box>
          : null
      )) : null

  );
}
